'use client'

import { useTranslate } from '@tolgee/react'
import { useSelector } from 'react-redux'
import SlotCounter, { SlotCounterRef } from 'react-slot-counter'

import Button from '@/components/common/Button'
import {
  selectIsPreviewMode,
  selectSectionSettingData,
} from '@/redux/customisation/selector'

import MenuLogo from '@/components/common/MenuLogo'
import { H3, P, SmallText } from '@/components/common/Typo'
import IconBag from '@/components/icons/IconBag'
import IconCalendar from '@/components/icons/IconCalendar'
import IconGlobe from '@/components/icons/IconGlobe'
import BadgeComponent from '@/components/sections/Header/Badge'
import { useModal } from '@/contexts/ModalContext'
import { usePreviewNavigationContext } from '@/contexts/PreviewNavigationProvider'
import useAppContext from '@/hooks/useAppContext'
import useAuth from '@/hooks/useAuth'
import useCustomisation, { PagePath } from '@/hooks/useCustomisation'
import useReminderOrderTrigger from '@/hooks/useReminderOrderTrigger'
import useResponsive from '@/hooks/useResponsive'
import useTracker from '@/hooks/useTracker'
import {
  ODDLE_PASS_CLIENT_ID,
  ODDLE_PASS_REDIRECT_URL,
} from '@/libs/config/client'
import { MODAL_TYPES } from '@/libs/config/modal'
import { NextLink, useRouter } from '@/libs/tolgee/navigation'
import {
  selectDetailMenuSite,
  selectFeatureToggles,
  selectMeta,
} from '@/redux/meta'
import { selectStoreOrder } from '@/redux/order'
import {
  useSignupCustomerMutation,
  useSyncOrderMethodMutation,
  useUpdatePickupLocationMutation,
} from '@/services/local'
import { useLazyGetEligiblePromotionsQuery } from '@/services/oms'
import { decodeToken } from '@/utils/jwt'
import { getCurrentLocale, languages } from '@/utils/localisation'
import { getOrderItemQuantity } from '@/utils/product'
import { dateToRequestString } from '@/utils/routes'
import { useParams, usePathname, useSearchParams } from 'next/navigation'
import {
  AlignJustifyIcon,
  cn,
  DialogTitle,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Separator,
  toast,
  XIcon as IconClose,
} from 'oddle-shop-monorepo.design-system'
import { useCallback, useEffect, useRef, useState } from 'react'
import UserButton, { pagesArray } from './UserButton'

const ButtonTextClassName =
  'text-base md:text-base lg:text-base leading-[20px] md:leading-[20px] lg:leading-[20px]'

const Header = ({ pageName }: { pageName: PagePath }) => {
  const { t } = useTranslate()
  const sectionSettingData = useSelector(selectSectionSettingData)
  const { closeModal, openModal } = useModal()
  const { data, refetch } = useAuth()
  const storeOrder = useSelector(selectStoreOrder)
  const searchParams = useSearchParams()
  const syncedOrder = useRef(false)
  const { pushWithPreview } = usePreviewNavigationContext()
  const hasOpenedRef = useRef(false)
  const { replaceWithPreview } = usePreviewNavigationContext()
  const pathname = usePathname()
  const router = useRouter()
  const params = useParams()
  const { mobile: isMobile } = useResponsive()
  const { currencySymbol } = useSelector(selectDetailMenuSite) || {}

  const initializeRef = useRef<boolean>(false)
  const counterRef = useRef<SlotCounterRef>(null)

  const currentLocale = getCurrentLocale(pathname)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { enableAccountLevelMarketingSubscription } =
    useSelector(selectFeatureToggles) || {}

  const {
    formatMoney,
    formatShortDate,
    prepareToCheckout,
    redirectToCheckout,
  } = useAppContext()
  const {
    notifyLogin,
    segmentTrackerSignedOut,
    segmentTrackerSignedIn,
    notifyAccountCompleteRegistration,
  } = useTracker()

  const [syncOrderMethod] = useSyncOrderMethodMutation()
  const [updatePickupLocation] = useUpdatePickupLocationMutation()
  const [signupCustomer] = useSignupCustomerMutation()

  const { pageOptions } = useCustomisation(pageName)
  const metaState = useSelector(selectMeta)
  const menuDetails = useSelector(selectDetailMenuSite)
  const isPreviewMode = useSelector(selectIsPreviewMode)

  const [getEligiblePromotions] = useLazyGetEligiblePromotionsQuery()

  useReminderOrderTrigger(storeOrder.data)

  const paramSelectDiningPreference = searchParams?.get(
    'selectDiningPreference'
  )
  const paramSelectedStoreId = searchParams?.get('storeId')

  // Effect: open modal if selectDiningPreference=true in query
  useEffect(() => {
    const shouldOpen = searchParams?.get('selectDiningPreference') === 'true'
    if (shouldOpen && !hasOpenedRef.current) {
      openModal(MODAL_TYPES.PICKUP_DELIVERY_SELECTION, {
        onAfterClose: () => {
          // Remove the query param when modal closes
          const newParams = new URLSearchParams(searchParams.toString())
          newParams.delete('selectDiningPreference')
          // Only use window.location on client side to prevent hydration errors
          if (typeof window !== 'undefined') {
            replaceWithPreview(
              `${window.location.pathname}${
                newParams.toString() ? '?' + newParams.toString() : ''
              }`,
              { scroll: false }
            )
          }
        },
        onFinished: () => openModal(MODAL_TYPES.CART_DRAWER),
      })
      hasOpenedRef.current = true
    }
    // Reset ref if param is gone
    if (!shouldOpen) {
      hasOpenedRef.current = false
    }
  }, [searchParams, openModal, replaceWithPreview])

  useEffect(() => {
    async function syncPickupOrder() {
      await syncOrderMethod({
        storeOrderId: storeOrder.data?.id,
        method: 'pickup',
      })

      if (paramSelectedStoreId) {
        await updatePickupLocation({
          storeId: paramSelectedStoreId,
          storeOrderId: storeOrder?.data?.id,
        })
        openModal(MODAL_TYPES.DATE_TIME_SELECTION)
        return
      }
      openModal(MODAL_TYPES.PICKUP_STORE_SELECTION)
    }

    async function signDeliveryOrder() {
      await syncOrderMethod({
        storeOrderId: storeOrder.data?.id,
        method: 'delivery',
      })
      openModal(MODAL_TYPES.DELIVERY_ADDRESS_SELECTION)
    }

    if (
      paramSelectDiningPreference === 'pickup' &&
      storeOrder?.data?.id &&
      !syncedOrder.current &&
      !pageOptions.withoutHeader
    ) {
      syncedOrder.current = true
      const timeout = setTimeout(syncPickupOrder, 1000)
      return () => {
        clearTimeout(timeout)
      }
    }

    if (
      paramSelectDiningPreference === 'delivery' &&
      storeOrder?.data?.id &&
      !syncedOrder.current &&
      !pageOptions.withoutHeader
    ) {
      syncedOrder.current = true
      const timeout = setTimeout(signDeliveryOrder, 1000)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [storeOrder?.data?.id, pageOptions.withoutHeader])

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      setIsScrolled(scrollTop > 0)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const goToPromotionOrCheckoutStep = useCallback(async () => {
    const response = await getEligiblePromotions(menuDetails?.storeOrderId)

    const { promoCodeEnabled, promotions } = response?.data?.payload || {}
    if (promoCodeEnabled || (promotions || []).length > 0) {
      openModal(MODAL_TYPES.CHECKOUT_PROMOTION_SELECTION)
      return
    }
    await prepareToCheckout({
      onReadyToCheckout: (storeOrder) => redirectToCheckout(storeOrder.id),
      onNotReadyToCheckout: (errorCode) =>
        openModal(MODAL_TYPES.ERROR_MODAL, {
          errorCode,
          onConfirm: closeModal,
        }),
      onShouldRefresh: () => undefined,
    })
  }, [
    getEligiblePromotions,
    openModal,
    prepareToCheckout,
    redirectToCheckout,
    menuDetails?.storeOrderId,
  ])

  useEffect(() => {
    if (
      window.opw &&
      menuDetails?.menu?.name &&
      menuDetails?.countryCode &&
      !initializeRef.current
    ) {
      window.opw.initializeApp(
        ODDLE_PASS_CLIENT_ID,
        ODDLE_PASS_REDIRECT_URL,
        (params?.locale as string) || 'en_SG',
        {
          menuName: menuDetails?.menu?.name,
          state: {
            country: menuDetails?.menu?.country,
          },
          countryCode: menuDetails?.countryCode,
          disableMarketingMenu: false,
          useIframe: true,
          enabledAccMarketingSub: Boolean(
            enableAccountLevelMarketingSubscription
          ),
        }
      )
    }
  }, [
    menuDetails?.countryCode,
    enableAccountLevelMarketingSubscription,
    menuDetails?.menu?.country,
    menuDetails?.menu?.name,
    params?.locale,
  ])

  useEffect(() => {
    if (window.opw) {
      window.opw.addListener(async (action, message) => {
        if (action === 'initialized') {
          initializeRef.current = true
          return
        }
        if (isPreviewMode) {
          toast.error(t('Cannot login on preview mode'))
          return
        }
        let userData
        if (
          [
            'signin_success',
            'facebook_signin_success',
            'signup_success',
          ].includes(action)
        ) {
          userData = await refetch()
          const parsedAccessToken = decodeToken(
            window.localStorage.getItem('accessToken')
          )
          if (
            userData.data?.data?.errorCode ===
              'customer_require_verification' &&
            (parsedAccessToken.state || {}).requireVerification
          ) {
            await window.opw.verificationAccount(
              menuDetails?.menu?.id as string,
              {
                path: '/[locale]',
                as: `/${params?.locale}`,
              }
            )
            return
          }
          if (userData.data?.data?.payload?.id) {
            const continueWithOddlePass =
              (parsedAccessToken.state || {}).continueWithOddlePass || false
            const authMethod = (parsedAccessToken.state || {}).authMethod
            const {
              ocaId,
              firstName,
              lastName,
              email,
              phone,
              hasVerifiedPhone,
            } = userData.data.data.payload

            segmentTrackerSignedIn({
              id: ocaId,
              firstName,
              lastName,
              email,
              phone,
              continueWithOddlePass,
              country: menuDetails?.menuCountry as string,
              phoneIsVerified: hasVerifiedPhone,
              authMethod,
            })
            toast.success(message)
            notifyLogin(authMethod)
            if ((parsedAccessToken.state || {}).goToPromotionOrCheckoutStep) {
              return goToPromotionOrCheckoutStep()
            }
            return
          }
          toast.error(userData.data?.data?.errorCode)
          return
        }
        if (action === 'signout_success') {
          window.localStorage.removeItem('accessToken')
          toast.success(t('Successfully logged out.'))
          segmentTrackerSignedOut({
            country: menuDetails?.menuCountry as string,
          })
          await refetch()
          window.location.reload()
          return
        }
        if (action === 'verification_success') {
          userData = await refetch()
          const parsedToken = decodeToken(
            window.localStorage.getItem('accessToken')
          )
          // this logic make sure just process if browser not support 3rd party cookies, so OddlePass just can be use by redirect flow
          // after customer verified their information, OddlePass will redirect back to OddleShop and then should handle error customer_require_verification to call signUpAction
          if (
            userData.data?.data?.errorCode ===
              'customer_require_verification' &&
            (parsedToken.state || {}).action === 'verification_success'
          ) {
            const signUpResponse = await signupCustomer({
              ...parsedToken.state.data,
              dob: dateToRequestString(parsedToken.state.data?.dob),
              subdomain: metaState.cartName,
              storeOrderId: menuDetails?.storeOrderId,
              menuId: menuDetails?.menu?.id,
            })
            if (userData?.data?.data?.payload?.id) {
              notifyAccountCompleteRegistration(
                signUpResponse.data?.data?.payload || {}
              )
            }
            userData = await refetch()
          }
          if (userData?.data?.data?.errorCode) {
            toast.error(t(userData?.data?.data?.errorCode))
            return
          }
          toast.success((parsedToken.state || {}).message)
          if ((parsedToken.state || {}).goToPromotionOrCheckoutStep) {
            return goToPromotionOrCheckoutStep()
          }
          if (
            ((parsedToken.state || {}).configsCallback || {})
              .redirectToCheckoutPage
          ) {
            const {
              ocaId,
              firstName,
              lastName,
              email,
              phone,
              hasVerifiedPhone,
            } = userData.data?.data?.payload as Oms.UserData
            segmentTrackerSignedIn({
              id: ocaId,
              firstName,
              lastName,
              email,
              phone,
              country: menuDetails?.menuCountry as string,
              continueWithOddlePass: false,
              phoneIsVerified: hasVerifiedPhone,
              authMethod: (parsedToken.state || {}).authMethod,
            })
            pushWithPreview(parsedToken.state.configsCallback.path)
          }
        }
      })
    }
  }, [
    goToPromotionOrCheckoutStep,
    isPreviewMode,
    menuDetails?.menuCountry,
    menuDetails?.menu?.id,
    notifyAccountCompleteRegistration,
    notifyLogin,
    params?.locale,
    refetch,
    pushWithPreview,
    segmentTrackerSignedIn,
    segmentTrackerSignedOut,
    signupCustomer,
    t,
  ])

  useEffect(() => {
    if (storeOrder.data?.total) {
      counterRef.current?.startAnimation()
    }
  }, [storeOrder.data?.total])

  if (pageOptions.withoutHeader) {
    return null
  }

  const { settings: customisationSettings = {} } =
    sectionSettingData?.sections?.['header'] || {}

  const { homepageUrl: _homepageUrl } = customisationSettings

  const itemsCount = storeOrder.data?.orderItems
    ? getOrderItemQuantity(storeOrder.data?.orderItems)
    : 0

  const collectionTime = storeOrder.data?.collectionTime
  const collectionDate = storeOrder.data?.collectionDate
  const totalAmount = storeOrder.data?.total

  const { isPickup, storeAddress, defaultStoreId, deliveryAddress } =
    storeOrder.data || {}

  const isAppointmentReady =
    (isPickup && !!storeAddress && !defaultStoreId) ||
    (!isPickup && !!deliveryAddress)
  const enabledReservation = menuDetails?.channelEnabled?.reserve

  const HeaderMenuLogo = () => {
    return (
      <NextLink href={_homepageUrl || '/'} className="cursor-pointer">
        <MenuLogo logoClassName="max-w-[135px]" />
      </NextLink>
    )
  }

  const HeaderDeliveryDateTime = () => {
    return (
      <div
        className={cn(
          'text-header-link-color h-[42px] text-left text-sm duration-500 ease-in-out',
          !isScrolled || !isAppointmentReady
            ? 'h-0 w-10 -translate-y-[20px] opacity-0'
            : 'translate-y-0 opacity-100'
        )}
        onClick={() => openModal(MODAL_TYPES.DATE_TIME_SELECTION)}
      >
        <div>
          <SmallText className="text-header-link-color">
            {isPickup
              ? t('Pickup for {collectionDate}', {
                  collectionDate: formatShortDate(collectionDate),
                })
              : t('Delivery for {collectionDate}', {
                  collectionDate: formatShortDate(collectionDate),
                })}
          </SmallText>
        </div>
        <div>
          <SmallText className="text-header-link-color">
            {collectionTime}
          </SmallText>
        </div>
      </div>
    )
  }

  const PriceWithItemNumber = () => {
    if (!currencySymbol) return null
    const formattedAmount = formatMoney(totalAmount)
    const numericValue = formattedAmount.replace(currencySymbol, '')

    return (
      <div
        className={cn(
          'flex h-[42px] flex-row items-center justify-end pr-2 duration-500 ease-in-out',
          itemsCount > 0
            ? 'translate-y-0 opacity-100'
            : 'h-0 translate-y-[20px] opacity-0'
        )}
        onClick={() => openModal(MODAL_TYPES.CART_DRAWER)}
      >
        <Button
          className={cn(
            'text-header-link-color flex cursor-pointer flex-nowrap gap-0 border-none px-1 text-base no-underline',
            !(itemsCount > 0) ? 'absolute -top-[100px] opacity-0' : '',
            ButtonTextClassName
          )}
          variant="link"
        >
          {formattedAmount.startsWith(currencySymbol) ? currencySymbol : null}
          <SlotCounter
            value={numericValue}
            ref={counterRef}
            animateOnVisible={false}
            autoAnimationStart={false}
          />
          {!formattedAmount.startsWith(currencySymbol) ? currencySymbol : null}
        </Button>
        <div
          key={itemsCount}
          data-testid="badge"
          className={cn(
            'bg-header-link-color animate-badge flex h-[26px] min-w-[26px] items-center justify-center rounded-full',
            !(itemsCount > 0) ? 'absolute -top-[100px] opacity-0' : ''
          )}
        >
          <P
            className={cn(
              'text-header-background-color m-0 mb-0.5 pl-1.5 pr-1.5 text-center font-bold',
              itemsCount > 9 && 'text-sm'
            )}
          >
            {itemsCount > 99 ? '99+' : itemsCount}
          </P>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'font-headings bg-header-background-color sticky z-[3] block h-[60px] w-full transition-shadow duration-300 md:h-auto md:min-h-[80px]'
      )}
      style={{
        top: 'env(safe-area-inset-top, 0)',
        paddingTop: 'env(safe-area-inset-top, 0)',
      }}
      id="oddle-shop-header"
    >
      {isMobile ? (
        <div className="flex h-full max-w-[1200px] flex-row items-center px-3 py-2">
          {/*if not scrolled => show menu log with reserve and order menu text + hamburger menu*/}
          {/*if scrolled and !isAppointmentReady => show Booking a table and Order with the hamburger menu. When itemsCount changes, show the itemsCount near by Order*/}
          {/*if scrolled and isAppointmentReady => show the delivery date time + total amount + number itemsCount*/}
          <>
            {!enabledReservation ? (
              <div className="flex w-full flex-row items-center justify-between">
                <div
                  className={cn(
                    'relative',
                    isScrolled && !isAppointmentReady ? 'w-10' : ''
                  )}
                >
                  <div
                    className={cn(
                      'flex h-[42px] items-center duration-500 ease-in-out',
                      !isScrolled
                        ? 'translate-y-0 opacity-100'
                        : 'h-0 -translate-y-[20px] opacity-0'
                    )}
                  >
                    <HeaderMenuLogo />
                  </div>
                  <HeaderDeliveryDateTime />
                </div>
                <div
                  className={cn(
                    'relative flex w-fit justify-center transition-all duration-500 ease-in-out',
                    isScrolled && !isAppointmentReady ? 'w-full' : ''
                  )}
                >
                  <Button
                    className={cn(
                      'h-[42px] cursor-pointer flex-row border-none px-2 text-base no-underline',
                      itemsCount > 0
                        ? 'absolute -top-[100px] opacity-0'
                        : 'translate-y-0 opacity-100',
                      ButtonTextClassName
                    )}
                    variant="link"
                    onClick={() => openModal(MODAL_TYPES.CART_DRAWER)}
                  >
                    <SmallText className="text-header-link-color">
                      {t('Order')}
                    </SmallText>
                  </Button>
                  <PriceWithItemNumber />
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-row items-center justify-between">
                <div className="relative w-full">
                  <div
                    className={cn(
                      'flex h-[42px] w-full items-center justify-between duration-500 ease-in-out',
                      isScrolled
                        ? 'h-0 -translate-y-[20px] opacity-0'
                        : 'translate-y-0 opacity-100'
                    )}
                  >
                    <HeaderMenuLogo />
                    <div className="flex flex-row">
                      <NextLink href="/reservations">
                        <Button
                          className={cn(
                            'text-header-link-color cursor-pointer border-none px-2 text-base no-underline md:px-4',
                            isScrolled ? 'hidden' : 'block',
                            ButtonTextClassName
                          )}
                          variant="link"
                        >
                          <SmallText className="text-header-link-color">
                            {t('Reserve')}
                          </SmallText>
                        </Button>
                      </NextLink>
                      <Button
                        className={cn(
                          'text-header-link-color cursor-pointer flex-row border-none pl-2 pr-1 text-base no-underline',
                          isScrolled ? 'hidden' : 'flex',
                          ButtonTextClassName
                        )}
                        variant="link"
                        onClick={() => openModal(MODAL_TYPES.CART_DRAWER)}
                      >
                        <SmallText className="text-header-link-color">
                          {isAppointmentReady ? t('Cart') : t('Order')}
                        </SmallText>
                        {itemsCount > 0 && (
                          <div
                            key={itemsCount}
                            data-testid="badge"
                            className={cn(
                              'bg-header-link-color animate-badge mr-2 flex h-[26px] min-w-[26px] items-center justify-center rounded-full'
                            )}
                          >
                            <P
                              className={cn(
                                'text-header-background-color m-0 mb-0.5 pl-1.5 pr-1.5 text-center font-bold',
                                itemsCount > 9 && 'text-sm'
                              )}
                            >
                              {itemsCount > 99 ? '99+' : itemsCount}
                            </P>
                          </div>
                        )}
                      </Button>
                    </div>
                  </div>
                  <div
                    className={cn(
                      'flex h-[42px] w-full items-center justify-around duration-500 ease-in-out',
                      !isScrolled
                        ? 'h-0 translate-y-[20px] opacity-0'
                        : isAppointmentReady && itemsCount > 0
                        ? 'hidden'
                        : 'translate-y-0 opacity-100'
                    )}
                  >
                    <NextLink href="/reservations" className="cursor-pointer">
                      <Button
                        className={cn(
                          'text-header-link-color border-none px-2 text-base no-underline md:px-4',
                          !isScrolled || (isAppointmentReady && itemsCount > 0)
                            ? 'absolute -top-[100px] opacity-0'
                            : '',
                          ButtonTextClassName
                        )}
                        variant="link"
                      >
                        <IconCalendar className="text-header-link-color !h-4 !w-4" />
                        <SmallText className="text-header-link-color">
                          {t('Book a Table')}
                        </SmallText>
                      </Button>
                    </NextLink>
                    <div
                      className={cn(
                        'bg-header-link-color my-auto inline-block h-[30px] min-h-[1em] w-0.5 self-stretch',
                        !isScrolled || (isAppointmentReady && itemsCount > 0)
                          ? 'absolute -top-[100px] opacity-0'
                          : ''
                      )}
                    />
                    <Button
                      className={cn(
                        'text-header-link-color cursor-pointer border-none px-2 text-base no-underline md:px-4',
                        !isScrolled || (isAppointmentReady && itemsCount > 0)
                          ? 'absolute -top-[100px] opacity-0'
                          : '',
                        ButtonTextClassName
                      )}
                      variant="link"
                      onClick={() => openModal(MODAL_TYPES.CART_DRAWER)}
                    >
                      <IconBag className="!h-4 !w-4" />
                      <SmallText className="text-header-link-color">
                        {t('Order')}
                      </SmallText>
                      {itemsCount > 0 && (
                        <div
                          key={itemsCount}
                          data-testid="badge"
                          className={cn(
                            'bg-header-link-color animate-badge flex h-[26px] min-w-[26px] items-center justify-center rounded-full',
                            !isScrolled ? 'absolute -top-[100px] opacity-0' : ''
                          )}
                        >
                          <P
                            className={cn(
                              'text-header-background-color m-0 mb-0.5 pl-1.5 pr-1.5 text-center font-bold',
                              itemsCount > 9 && 'text-sm'
                            )}
                          >
                            {itemsCount > 99 ? '99+' : itemsCount}
                          </P>
                        </div>
                      )}
                    </Button>
                  </div>
                  <div
                    className={cn(
                      'flex h-[42px] w-full items-center justify-between duration-500 ease-in-out',
                      !isScrolled
                        ? 'h-0 translate-y-[20px] opacity-0'
                        : isAppointmentReady && itemsCount > 0
                        ? 'translate-y-0 opacity-100'
                        : 'hidden'
                    )}
                  >
                    <HeaderDeliveryDateTime />
                    <PriceWithItemNumber />
                  </div>
                </div>
              </div>
            )}
          </>
          <Drawer
            direction="right"
            open={isDrawerOpen}
            onOpenChange={setIsDrawerOpen}
          >
            <DrawerOverlay className="bg-back/40 fixed inset-0" />
            <DrawerTrigger aria-label="Open menu">
              <div className="flex h-10 w-10 items-center justify-center">
                <AlignJustifyIcon className="text-header-link-color !h-8 !w-8" />
              </div>
            </DrawerTrigger>
            <DrawerContent className="bg-container -right-[1px] left-auto flex h-full w-4/5 rounded-none border-0 p-5 outline-none">
              <DrawerHeader className="bg-container relative p-0 text-lg font-bold">
                <DialogTitle asChild>
                  <div className="mt-7 flex items-center justify-between">
                    <MenuLogo logoClassName="max-w-full h-full" showStoreName />
                  </div>
                </DialogTitle>
                <div className="absolute right-0">
                  <DrawerClose>
                    <IconClose className="text-headings size-7" />
                  </DrawerClose>
                </div>
              </DrawerHeader>
              <div className="flex flex-col gap-2.5">
                <Button
                  className={cn(
                    'text-header-link-color cursor-pointer justify-start border-none px-2 text-base no-underline md:px-4',
                    ButtonTextClassName
                  )}
                  variant="link"
                  onClick={() => pushWithPreview('/')}
                >
                  <H3 className="text-header-link-color font-medium">
                    {t('Order')}
                  </H3>
                </Button>
                <Button
                  className={cn(
                    'text-header-link-color cursor-pointer justify-start border-none px-2 text-base no-underline md:px-4',
                    ButtonTextClassName
                  )}
                  variant="link"
                  onClick={() => pushWithPreview('/stores')}
                >
                  <H3 className="text-header-link-color font-medium">
                    {t('Stores')}
                  </H3>
                </Button>
                <Separator />
                {data?.data?.payload?.id ? (
                  <>
                    {pagesArray.map((page) => (
                      <Button
                        key={page.id}
                        className={cn(
                          'text-header-link-color cursor-pointer justify-start border-none px-2 text-base no-underline md:px-4',
                          ButtonTextClassName
                        )}
                        variant="link"
                        onClick={() => pushWithPreview(page.as)}
                      >
                        <H3 className="text-header-link-color font-medium">
                          {t(page.name)}
                        </H3>
                      </Button>
                    ))}
                    <Button
                      className={cn(
                        'text-header-link-color cursor-pointer justify-start border-none px-2 text-base no-underline md:px-4',
                        ButtonTextClassName
                      )}
                      variant="link"
                      onClick={async (e) => {
                        e.preventDefault()
                        await window.opw.signOut(data.data.payload.ocaId)
                        window.location.reload()
                      }}
                    >
                      <H3 className="text-header-link-color font-medium">
                        {t('Sign Out')}
                      </H3>
                    </Button>
                  </>
                ) : (
                  <UserButton
                    truncate
                    onSignInClick={() => setIsDrawerOpen(false)}
                  />
                )}
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      ) : (
        <div className="mx-auto flex h-full max-w-[1200px] items-center justify-between px-3 py-2 md:px-10 md:py-3">
          <div className="flex cursor-pointer items-center">
            <NextLink href={_homepageUrl || '/'}>
              <MenuLogo />
            </NextLink>
          </div>
          <div className="hidden items-center gap-1 text-base md:flex md:gap-4">
            <NextLink href="/">
              <Button
                className={cn(
                  'text-header-link-color cursor-pointer border-none px-2 text-base no-underline md:px-4',
                  ButtonTextClassName
                )}
                variant="link"
              >
                {t('Order')}
              </Button>
            </NextLink>
            {menuDetails?.channelEnabled?.reserve && (
              <NextLink href="/reservations">
                <Button
                  className={cn(
                    'text-header-link-color cursor-pointer border-none px-2 text-base no-underline md:px-4',
                    ButtonTextClassName
                  )}
                  variant="link"
                >
                  {t('Reservation')}
                </Button>
              </NextLink>
            )}
            <NextLink href="/stores">
              <Button
                className={cn(
                  'cursor-pointere text-header-link-color border-none px-2 text-base no-underline md:px-4',
                  ButtonTextClassName
                )}
                variant="link"
              >
                {t('Store')}
              </Button>
            </NextLink>
            {(menuDetails?.locales?.length || 0) > 1 && (
              <Select
                value={currentLocale}
                onValueChange={(locale) => {
                  if (!pathname) return
                  // Extract the path without the current locale
                  const pathSegments = pathname.split('/').filter(Boolean)
                  const pathWithoutLocale =
                    pathSegments.length > 0 && pathSegments[0] === currentLocale
                      ? pathSegments.slice(1).join('/')
                      : pathSegments.join('/')

                  // Use the Tolgee navigation router to handle locale routing
                  const newPath = pathWithoutLocale
                    ? `/${pathWithoutLocale}`
                    : '/'

                  // Change the locale in the URL by navigating to the path without locale
                  // The Tolgee router will automatically add the new locale prefix
                  router.replace(newPath, { locale })
                }}
              >
                <SelectTrigger className="text-header-link-color border-none focus-visible:ring-0">
                  <IconGlobe />
                  {currentLocale && languages[currentLocale]
                    ? languages[currentLocale]
                    : ''}
                </SelectTrigger>
                <SelectContent className="bg-container">
                  {menuDetails?.locales.map((locale) => (
                    <SelectItem
                      key={locale}
                      value={locale}
                      className="focus-visible:ring-0"
                    >
                      {languages[locale]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <UserButton />
            <div data-testid="cartIconButton" className="flex items-center">
              <Button
                variant="ghost"
                onClick={() => openModal(MODAL_TYPES.CART_DRAWER)}
                className={cn(
                  'text-header-link-color border-none p-0 [&_svg]:size-8',
                  ButtonTextClassName
                )}
              >
                <BadgeComponent />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Header
